import React, { useEffect, useRef, useState } from "react";
import { FaEdit, FaTimes } from "react-icons/fa";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";
import styles from "../../styles/components/profile/EditCollectionModal.module.css";

/**
 * Modal to rename a collection.
 * Cover image is always automatic (latest saved post).
 */
const EditCollectionModal = ({ collection, userId, onClose, onUpdated }) => {
  const [name, setName] = useState(collection.name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    setName(collection.name);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [collection.name]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name cannot be empty.");
      return;
    }
    if (trimmed === collection.name) {
      onClose();
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await api.patch(
        `/posts/collections/${encodeURIComponent(collection.name)}`,
        { name: trimmed }
      );
      onUpdated?.(res.data.collection);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to rename collection.");
    } finally {
      setSaving(false);
    }
  };

  /* close on Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <span className={styles.headerTitle}>
            <FaEdit size={14} /> Edit Collection
          </span>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.body}>
          {/* Cover preview (automatic) */}
          <div className={styles.coverPreview}>
            {collection.coverImage ? (
              <img
                src={getImageUrl(collection.coverImage)}
                alt="Cover"
                className={styles.coverImg}
              />
            ) : (
              <div className={styles.coverPlaceholder}>No cover yet</div>
            )}
            <span className={styles.coverHint}>Cover updates automatically</span>
          </div>

          <label className={styles.label}>Collection name</label>
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError("");
            }}
            maxLength={60}
          />

          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.saveBtn}
              disabled={saving || !name.trim()}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCollectionModal;
